import React from "react";

const TacticsToGetVisibilityVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson offers practical ways to build presence and personal brand through everyday visibility. It covers tactics like spotlighting your team, protecting credit, choosing work by return on visibility, teaching what you know, building networks, and becoming calm in chaos. It also introduces ideas like strategic absence, selective contribution, and small visibility upgrades so your impact becomes easier to notice. The core point is simple: visibility is not vanity, it is a choice in how you do it.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>If you're still unsure how to be present and build your personal brand, let's go through a few tactics you can start applying from today.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">1. Publicly congratulate your team.</h3>
    <p>Celebrate their wins, name the people who made it happen. It shows you notice details and that you lead with generosity.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">2. Stop others from taking credit for your work.</h3>
    <p>You don't need to be aggressive, just make sure your contributions are naturally recognized.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">3. Bring your personal brand into everything you do.</h3>
    <p>Whether you're known for problem-solving, creativity, or reliability, let your signature style shine through every project.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">4. Pick projects and volunteer strategically.</h3>
    <p>Choose tasks based on <strong>RO-V. Your return on visibility.</strong> If the task is high risk but does not give you visibility, it is not worth the risk.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">5. Host lunch-and-learns, seminars, or training.</h3>
    <p>Sharing knowledge not only helps you learn, but also builds your credibility. It puts your name in people's minds with very little effort, because you're simply sharing what you already know.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">6. Sometimes be absent.</h3>
    <p>It may sound odd, but if things go a little chaotic when you're away, that's a win. People notice your absence, and realise your value. Strategic absence speaks volumes about your impact.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">7. Create a network inside and outside the company.</h3>
    <p>Connections amplify your reach and help people see the bigger picture of your work. Whether it's outside or inside your network is your opportunity for bigger things.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">8. Create a thought leadership footprint internally.</h3>
    <p>Share small insights, frameworks, or tips in emails, forums, or meetings. Over time, people start associating your name with smart thinking.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">9. Be the calm in chaos.</h3>
    <p>When stress hits, your composure makes you memorable, people, especially the ones in charge, notice who can think clearly when everyone else is panicking.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">10. Create Scarcity Through Contribution.</h3>
    <p>Volunteer selectively for high-impact moments, not everything. When you do contribute, people notice because it's meaningful and rare.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">11. Invest in small visibility wins.</h3>
    <p>A good webcam and keeping your camera on during virtual calls go a long way. People remember faces, not just names, visual feedback makes your presence felt.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">12. Volunteer for high-visibility operational tasks.</h3>
    <p>Offer to support key stakeholders with PMO or EA-style help, managing calendars, scheduling visits, or even recommending restaurants. It's proximity with purpose and ensures you stay on their radar.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">13. Mirror their interests.</h3>
    <p>If your stakeholder is passionate about cars, golf, or art, learn enough to have an intelligent chat. Shared interests build quiet connection, and top management values partners, not followers.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">14. Travel proximity is free visibility.</h3>
    <p>If a leader you work with is on the same trip, try to sit near them. It gives you hours of free visibility and builds more goodwill than months of meetings.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">15. Be strategically visible in the room.</h3>
    <p>Sit in the front row where you're seen. Ask sharp, researched questions. Even if it ruffles a few feathers, this "strategic nuisance" makes you memorable, and memorable people move forward.</p>

    <p><strong>Remember, visibility isn't vanity, it's strategic authenticity.</strong> Many people tell themselves visibility isn't for them, that it feels forced or uncomfortable. But the truth is, you have to do it. The mistake is thinking there's only one way.</p>

    <p>You don't need to copy anyone's playbook. You don't have to follow every tip or trick here.</p>

    <p><strong>Find your own rhythm.</strong></p>

    <p>You cannot avoid visibility. How you do it is your choice.</p>
    <p>But doing it, that's <strong>non-negotiable</strong>.</p>

    <p><strong>Which of these tactics are you going to use?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default TacticsToGetVisibilityVideo;

